/**
 * Catálogo de referência Lunins — vitrine oficial (site único institucional + vendas).
 * Preços de referência no site; descrições voltadas a cliente, gestores e equipe comercial.
 * Curadoria comercial Lunins — preços de referência alinhados ao catálogo oficial.
 */
export type ItemDestaque = {
  id: string;
  nome: string;
  categoria: string;
  precoAVista: number;
  parcelas: number;
  parcelaValor: number;
  destaque?: string;
  /** Texto editorial longo — página de produto e SEO */
  descricaoLonga: string;
  beneficios: string[];
  indicadoPara: string;
  cuidados?: string;
  /** Parceria / linha fabricante (transparência com cliente) */
  fabricanteReferencia?: string;
  /**
   * Foto do produto: `public/images/catalogo/{slug}.webp|jpg` (acervo Lunins).
   * Ex.: `/images/catalogo/massageador-pistola.webp`
   */
  imagemArquivo?: string;
  /** Foto em CDN (quando preferível ao arquivo local em public/) */
  imagemNuvemUrl?: string;
};

export const CATEGORIAS_LOJA = [
  'Vibroterapia',
  'Shiatsu',
  'Assentos',
  'Para os pés',
  'Massageadores manuais',
  'Cintas',
] as const;

/**
 * Loja pública curada para refletir a Nuvem Shop.
 * Mantemos aqui apenas os itens que devem aparecer em `/loja`.
 */
export const ITENS_LOJA_PUBLICA_IDS = [
  'massageador-manual',
  'massageador-relax-confort-sem-fio',
  'cinta-fisiopower',
  'colete-shiatsu',
  'almofada-shiatsu-roller',
  'almofada-shiatsu-lumina',
  'plataforma-reflexologia',
  'massageador-pes-shiatsu-pro',
  'assento-shiatsu-premium',
  'car-america',
  'massageador-pistola',
  'esteira-massageadora-still',
  'esteira-vibro-neck-shoulder',
  'car-relax-shoulder',
  'cadeira-shiatsu-still',
  'cadeira-reclinavel',
] as const;

/** Links públicos da marca. (Acervo interno de mídia — fora do site.) */
export const LINKS_EXTERNOS = {
  instagram: 'https://www.instagram.com/luninsbr/',
  instagramReels: 'https://www.instagram.com/luninsbr/reels/',
  luninsOrg: 'https://www.lunins.org',
  /** Site institucional — seção Salas (mesmo id da home da vitrine) */
  luninsOrgSalas: 'https://www.lunins.org/#salas',
  /** Site próprio (HTML). Usar /index.html — a raiz do domínio ainda cai no tema WordPress. */
  tutoriais: 'https://tutoriais.lunins.org/index.html',
} as const;

/** Preços de referência — conferir sempre no PDV / loja dinâmica */
export const ITENS_DESTAQUE: ItemDestaque[] = [
  {
    id: 'massageador-manual',
    imagemArquivo: '/images/catalogo/massageador-manual.png',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/img-20240131-wa0017-ce3f200dd0c4a22d3f17067086831073-1024-1024.webp',
    nome: 'Massageador Manual (com fio) — Relax Confort',
    categoria: 'Massageadores manuais',
    precoAVista: 709,
    parcelas: 12,
    parcelaValor: 59.08,
    destaque: 'Percussão profissional com fio',
    fabricanteReferencia: 'Curadoria Lunins — especificações no fechamento com a equipe.',
    descricaoLonga:
      'O Relax Confort é um massageador manual profissional com alimentação por fio, de alta qualidade para uso em clínicas de estética ou em casa. Design moderno e estrutura leve para sessões mais longas sem cansar os braços. Massagem de percussão vigorosa para alívio de dores e tensões musculares do dia a dia, melhorando circulação e sensação de bem-estar. Duas velocidades (intensa e moderada) e três ponteiras para personalizar o tipo de massagem — ficha alinhada à curadoria Lunins.',
    beneficios: [
      'Percussão intensa com controle de velocidade — duas opções de ritmo.',
      'Três ponteiras para efeitos diferentes nas regiões do corpo.',
      'Leve e ergonômico para uso prolongado.',
      'Indicado para pescoço, cintura, abdômen, coxa, braço e pernas.',
    ],
    indicadoPara:
      'Quem busca alívio de stress e fadiga muscular com equipamento cabeado (sem bateria); quem viu o modelo na vitrine de referência e quer o mesmo perfil de produto.',
    cuidados:
      'Use com o aparelho ligado à tomada conforme manual. Evite pele lesionada ou inflamação aguda. Em dor persistente, procure orientação de saúde.',
  },
  {
    id: 'massageador-relax-confort-sem-fio',
    imagemArquivo: '/images/catalogo/massageador-relax-confort-sem-fio.png',
    imagemNuvemUrl:
      'https://salescdn.net/Vk_YxjwwLYBYIQG7oCfPK3b83ns=/adaptive-fit-in/500x0/prod/store/13248/medias/products/massageador-relaxetion-pro-af6bdff7-543a-43c1-ae4e-46a5a30cebef.webp',
    nome: 'Massageador Manual sem fio (Relaxetion Pro)',
    categoria: 'Massageadores manuais',
    precoAVista: 1099,
    parcelas: 12,
    parcelaValor: 91.58,
    destaque: 'Manual sem fio com 4 ponteiras',
    fabricanteReferencia: 'Referência FisioPower (MPRO300) — curadoria Lunins.',
    descricaoLonga:
      'O manual sem fio é uma opção prática para quem quer liberdade total de movimento em casa, no trabalho ou em ações. O formato portátil combina percussão/vibração com quatro ponteiras para personalizar a experiência por região do corpo. É uma escolha forte para quem busca aliviar tensão e fadiga muscular no dia a dia, sem depender de tomada.',
    beneficios: [
      'Uso sem fio para levar em qualquer ambiente com praticidade.',
      'Quatro ponteiras para adaptar a massagem conforme a região do corpo.',
      'Ajuda a aliviar tensões musculares e sensação de fadiga diária.',
      'Formato portátil para rotina em casa, escritório ou deslocamentos.',
    ],
    indicadoPara:
      'Quem prioriza mobilidade total e quer massageador manual sem fio para rotina de autocuidado ou uso em ações.',
    cuidados:
      'Recarregue conforme manual do fabricante. Não use sobre pele lesionada. Gestantes ou com dispositivos médicos: orientação profissional antes do uso.',
  },
  {
    id: 'cinta-fisiopower',
    imagemArquivo: '/images/catalogo/cinta-fisiopower.png',
    nome: 'Cinta Massageadora',
    categoria: 'Cintas',
    precoAVista: 1999,
    parcelas: 12,
    parcelaValor: 169,
    fabricanteReferencia: 'Equipamento comercializado pela Lunins — detalhes técnicos no atendimento oficial.',
    descricaoLonga:
      'A cinta massageadora envolve abdômen e região lombar com movimentos rítmicos que ajudam a relaxar a musculatura profunda sem exigir esforço ativo do usuário — ideal para quem passa muitas horas em pé ou sentado. Em projetos Lunins com órgãos públicos, equipamentos dessa categoria costumam ser muito bem avaliados em turnos administrativos e operacionais com sobrecarga postural. Em casa, vira um “ritual de desligamento” após o expediente: você posiciona a cinta, escolhe a intensidade e permite que o corpo baixe o ritmo cardiovascular e mental.',
    beneficios: [
      'Massagem contínua na região média do tronco — muito procurada por quem carrega estresse na lombar.',
      'Libera as mãos: sessão passiva enquanto você lê, descansa ou faz uma pausa guiada.',
      'Complementa protocolos de alongamento e respiração sugeridos nas salas de descompressão Lunins.',
      'Montagem e uso explicados em vídeo no Instagram @luninsbr.',
    ],
    indicadoPara:
      'Servidores e lideranças com dor ou peso na região lombar; quem busca experiência próxima à de equipamentos institucionais; famílias que querem um único aparelho versátil para mais de um morador.',
    cuidados:
      'Gestantes, pessoas com marcapasso ou dispositivos médicos implantados devem consultar o médico antes do uso. Respeite os tempos máximos indicados pelo fabricante.',
  },
  {
    id: 'colete-shiatsu',
    imagemArquivo: '/images/catalogo/colete-shiatsu.png',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/colete-de-massagem-therapic-roller-shoulder-bivolt-0e71eb60-1854-4f22-9d9d-f5fc29e36059-b951dd6da0da4d825917017607874705-1024-1024.webp',
    nome: 'Colete Shiatsu',
    categoria: 'Shiatsu',
    precoAVista: 2129,
    parcelas: 12,
    parcelaValor: 177.42,
    fabricanteReferencia: 'Linha shiatsu — orientações de uso com a equipe Lunins.',
    descricaoLonga:
      'O colete shiatsu reproduz a sensação de pressão e amassamento nas costas e ombros, regiões onde o estresse emocional costuma “fixar” musculatura. Para instituições, é um dos formatos mais pedidos em demonstrações Lunins: o usuário sente resultado em poucos minutos. Para a casa, significa ter sessões repetíveis de qualidade, com custo por uso muito inferior ao de atendimentos avulsos em clínicas — sem substituir tratamento médico, mas elevando muito o conforto e o sono após plantões ou telas prolongadas.',
    beneficios: [
      'Cobertura ampla das costas e ombros — onde a tensão do estresse moderno mais aparece.',
      'Programas de movimento que alternam direção e ritmo, simulando mãos experientes.',
      'Experiência próxima à de cadeiras e estações shiatsu usadas em ambientes corporativos.',
      'Integra bem com pausas regulares no trabalho híbrido (home office + repartição).',
    ],
    indicadoPara:
      'Quem trabalha com teclado e mouse o dia inteiro; líderes de equipe que querem presentear com mensagem de saúde mental; casais que dividem o equipamento em horários diferentes.',
    cuidados:
      'Não durma com o aparelho ligado sem supervisão. Ajuste a pressão ao seu limiar — desconforto agudo não é o objetivo.',
  },
  {
    id: 'almofada-shiatsu-roller',
    imagemArquivo: '/images/catalogo/almofada-shiatsu-roller.jpg',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/almofada-shiatsu-roller-detalhe-6896b63f945432c73b17254870838679-1024-1024.webp',
    nome: 'Almofada Infra-Blue (Shiatsu Roller)',
    categoria: 'Shiatsu',
    precoAVista: 1599,
    parcelas: 12,
    parcelaValor: 133.25,
    destaque: 'Shiatsu profundo, 2 motores, bivolt',
    fabricanteReferencia: 'Linha shiatsu — especificações completas no atendimento Lunins.',
    descricaoLonga:
      'A Almofada Infra-Blue (base Shiatsu Roller) une design moderno a dois motores de massagem para relaxamento muscular mais intenso. Formato anatômico, leve e confortável, com esferas internas, infravermelho e luz azul como experiência sensorial. Uso em várias regiões do corpo e higienização facilitada. Bivolt 110–220 V, 30 W, dimensões aproximadas 32×40×13 cm e cerca de 2,20 kg — conforme ficha técnica da curadoria Lunins.',
    beneficios: [
      'Massagem shiatsu profunda com dois motores.',
      'Design anatômico; esferas internas para sensação de amassamento.',
      'Versátil no corpo todo; fácil de limpar.',
      'Bivolt — prática em viagens e ambientes diferentes.',
    ],
    indicadoPara:
      'Quem busca almofada shiatsu potente entre sessões de trabalho; quem já viu a linha em vitrine ou eventos Lunins.',
    cuidados:
      'Respeite o tempo máximo de uso do manual. Interrompa se houver dor aguda ou dormência.',
  },
  {
    id: 'almofada-shiatsu-lumina',
    imagemArquivo: '/images/catalogo/almofada-shiatsu-lumina.png',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/almofada-confort-3d-bivolt-8afcc098-537e-4a78-b7fe-82307c1699f3-a253f5b6cc1a0b7f7c17129272388424-1024-1024.webp',
    nome: 'Almofada Shiatsu Lumina (Confort 3D)',
    categoria: 'Shiatsu',
    precoAVista: 2429,
    parcelas: 12,
    parcelaValor: 202.42,
    destaque: '12 esferas, corpo inteiro, bivolt',
    fabricanteReferencia: 'Curadoria Lunins — ficha técnica e bivolt no fechamento com a equipe.',
    descricaoLonga:
      'A Almofada Shiatsu Lumina (linha Confort 3D) foi pensada para quem chega exausto ao fim do dia: massagem eficaz para soltar tensão muscular, cansaço e desconforto em costas e lombar, com uso também em pescoço, cintura, pernas e pés. São 12 esferas internas de massagem que reproduzem sensação próxima ao shiatsu manual — confortável, versátil e simples de posicionar. Bivolt para uso em casa ou em viagens; preço de referência alinhado ao catálogo Lunins.',
    beneficios: [
      'Doze esferas para massagem mais envolvente nas regiões do tronco e membros.',
      'Uma peça para várias áreas: pescoço, costas, lombar, cintura, pernas e pés.',
      'Prática no dia a dia — ideal após trabalho intenso ou em pausas de descanso.',
      'Bivolt — mesma referência de produto divulgada na loja Lunins.',
    ],
    indicadoPara:
      'Quem quer almofada shiatsu com foco em costas e lombar; quem já conhece a linha Confort 3D; uso doméstico ou presente com alto valor percebido.',
    cuidados:
      'Siga tempos e intensidade do manual. Não use sobre feridas ou inflamações agudas. Em caso de dor persistente, busque orientação de saúde.',
  },
  {
    id: 'plataforma-reflexologia',
    imagemArquivo: '/images/catalogo/plataforma-reflexologia.png',
    nome: 'Plataforma de Reflexologia',
    categoria: 'Para os pés',
    precoAVista: 2699,
    parcelas: 12,
    parcelaValor: 224.92,
    descricaoLonga:
      'A plataforma de reflexologia combina estímulo nos pontos plantares com vibração controlada — ajudando circulação e sensação de leveza após longos períodos em pé ou caminhando em superfícies duras. Em salas Lunins, equipamentos para os pés são frequentemente relatados como “os que mais surpreendem” em pesquisas de satisfação: o alívio é rápido e perceptível. Em casa, vira ponto de encontro da família: cada pessoa pode usar por alguns minutos antes de dormir ou após treino leve.',
    beneficios: [
      'Estímulo plantar que conversa com rotinas de quem fica muito tempo em pé (bancos, hospitais, escolas, eventos).',
      'Pode auxiliar na recuperação pós-jornada — sensação de pernas mais leves.',
      'Uso em sessões curtas — fácil encaixar na rotina noturna.',
      'Didática simples: tutoriais em vídeo no @luninsbr mostram posição dos pés e duração sugerida.',
    ],
    indicadoPara:
      'Profissionais de saúde e educação em pé o dia todo; idosos com orientação médica para movimento leve; quem busca ritual de relaxamento antes de dormir.',
    cuidados:
      'Pessoas com neuropatia diabética grave ou feridas nos pés devem obter liberação profissional antes do uso.',
  },
  {
    id: 'massageador-pes-shiatsu-pro',
    imagemArquivo: '/images/catalogo/massageador-pes-shiatsu-pro.png',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/190801-fisio-massageador-pes5494-4f9ec2ad3f2eb2893817067094439281-1024-1024.webp',
    nome: 'Massageador de pés Shiatsu Pro',
    categoria: 'Para os pés',
    precoAVista: 2599,
    parcelas: 12,
    parcelaValor: 216.58,
    fabricanteReferencia: 'Massageador para pés — ficha completa no fechamento.',
    descricaoLonga:
      'Versão avançada do cuidado com os pés: combina compressão, rolagem e pontos de pressão tipo shiatsu em uma única estação. O investimento se justifica para quem já experimentou modelos básicos e quer profundidade e durabilidade superiores — perfil comum entre gestores e compradores institucionais que depois levam a experiência para casa. A Lunins posiciona esse equipamento como “spa de pés” residencial: poucos minutos diários podem alterar qualidade do sono e sensação de bem-estar geral, pois os pés concentram termin nervoso e tensão acumulada.',
    beneficios: [
      'Experiência mais completa que modelos compactos — ideal para uso familiar.',
      'Programas variados para diferentes sensibilidades plantares.',
      'Combina com outras práticas de descompressão (respiração, áudio guiado) oferecidas nas ações Lunins.',
      'Excelente conversa para quem já participou de rodas de massagem em eventos e conferências.',
    ],
    indicadoPara:
      'Famílias com mais de um usuário; quem sente peso e queimação nos pés ao fim do dia; presentes de alto valor percebido para parceiros institucionais.',
    cuidados:
      'Inicie com ciclos curtos até adaptação. Limpeza do revestimento conforme manual — higiene é essencial em uso compartilhado.',
  },
  {
    id: 'foot-pro-relax',
    imagemArquivo: '/images/home/produtos/foot-pro-relax.png?v=5',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/massagedor-pro-relax-bivolt-82878015-59ad-4535-a043-35827a91bd61-d4e764605d1f92fa8917097696230229-1024-1024.webp',
    nome: 'Foot Pro Relax',
    categoria: 'Para os pés',
    precoAVista: 3999,
    parcelas: 12,
    parcelaValor: 333.25,
    destaque: 'Novo massageador de pés Pro Relax, com bolsa de ar',
    fabricanteReferencia: 'Catálogo oficial Lunins (Nuvem) — Foot Massager Pro Relax.',
    descricaoLonga:
      'O Foot Pro Relax é o massageador para os pés da linha Pro Relax: massagem personalizada em pés, tornozelos e panturrilhas, no sofá, na poltrona ou na cama. Ajuste de velocidade, luz com aquecimento suave, vibração que pode ser ligada ou desligada, modo conforto e temporizador automático (5 a 30 minutos). É o modelo da linha com bolsa de ar para massagem nas laterais, em cima e nas plantas dos pés, além do sistema de roller — três tipos de massagem em um único aparelho.',
    beneficios: [
      'Bolsa de ar nas laterais, em cima e nas plantas dos pés, com sistema de roller.',
      'Velocidade, vibração, modo conforto e luz com aquecimento suave.',
      'Temporizador com desligamento automático de 5 a 30 minutos.',
      'Massagem em pés, tornozelos e panturrilhas sem sair de casa.',
    ],
    indicadoPara:
      'Quem quer o massageador de pés mais completo da linha Lunins; fadiga em pés e panturrilhas após o expediente; presente de alto valor percebido.',
    cuidados:
      'Inicie com ciclos curtos até adaptação. Pessoas com neuropatia, feridas nos pés ou circulação comprometida devem obter liberação profissional antes do uso.',
  },
  {
    id: 'assento-shiatsu-premium',
    imagemArquivo: '/images/catalogo/assento-shiatsu-premium.png',
    imagemNuvemUrl:
      'https://dcdn-us.mitiendanube.com/stores/003/962/478/products/190403-sandro3200-copy-4b7dafa9da36deb33117067092774552-1024-1024.webp',
    nome: 'Assento Shiatsu Premium',
    categoria: 'Assentos',
    precoAVista: 4439,
    parcelas: 12,
    parcelaValor: 369.92,
    destaque: 'Linha premium',
    descricaoLonga:
      'O assento shiatsu premium leva a experiência de cadeiras de massagem profissionais para cadeiras de escritório ou poltronas domésticas — sem ocupar o espaço de um equipamento de corpo inteiro. É a escolha de quem quer o máximo de resultado em sessões curtas durante o dia: reuniões longas, plantões ou home office pesado. Instituições que já adotaram Lunins reconhecem o formato “assento” como de alta adesão porque não intimida quem nunca usou massageador: basta sentar e escolher o programa.',
    beneficios: [
      'Instalação rápida — transforma uma cadeira comum em estação de massagem.',
      'Programas superiores de shiatsu e, em muitos modelos, calor opcional para relaxamento muscular.',
      'ROI em bem-estar percebido: menos queixas de dor, mais sensação de cuidado com a equipe.',
      'Design pensado para durar — investimento amortizado em anos de uso.',
    ],
    indicadoPara:
      'Lideranças e compradores que já conhecem resultados em campo com a Lunins; escritórios e home offices premium; quem não tem espaço para esteira ou cadeira fechada.',
    cuidados:
      'Verifique peso máximo suportado e fixação nas cadeiras. Não use sobre superfícies instáveis.',
  },
  {
    id: 'car-america',
    imagemArquivo: '/images/products/fisiopower/assento-shiatsu-still-p29.webp',
    imagemNuvemUrl:
      'https://salescdn.net/Hl3IlRFdMsM5hA16lJ6xR3yxtoA=/adaptive-fit-in/500x0/prod/store/13248/medias/products/assento-shiatsu-still-59368a40-6872-454b-92d4-793ce41b47fc.webp',
    nome: 'Car America (Assento Shiatsu Still)',
    categoria: 'Assentos',
    precoAVista: 2699,
    parcelas: 12,
    parcelaValor: 224.92,
    destaque: 'Carzin: casa, escritório e carro',
    fabricanteReferencia: 'Referência FisioPower (Assento Shiatsu Still/ATF500) — curadoria Lunins.',
    descricaoLonga:
      'O Car America é um assento shiatsu versátil para quem quer relaxamento de costas e lombar em diferentes ambientes. O mesmo equipamento pode ser usado no sofá, na cadeira do escritório e no carro, com modos automáticos e manuais para ajustar a experiência ao perfil de cada usuário. Na rotina corrida, é um produto de alto valor percebido porque transforma uma cadeira comum em um ponto de descompressão.',
    beneficios: [
      'Uso 3 em 1: casa, escritório e carro.',
      'Massagem nas costas com opção automática e manual.',
      'Vibração no assento com níveis ajustáveis.',
      'Formato prático para pausas curtas ao longo do dia.',
    ],
    indicadoPara:
      'Quem passa horas sentado, dirige com frequência ou busca um assento de massagem versátil para vários ambientes.',
    cuidados:
      'Use em cadeira firme e estável. Não utilize deitado sobre o equipamento. Em dor persistente ou condição de coluna, busque orientação profissional.',
  },
  {
    id: 'massageador-pistola',
    imagemArquivo: '/images/catalogo/massageador-pistola.webp',
    nome: 'Massageador Pistola',
    categoria: 'Massageadores manuais',
    precoAVista: 1199,
    parcelas: 12,
    parcelaValor: 99.92,
    fabricanteReferencia: 'Massageador portátil — uso orientado nos tutoriais @luninsbr.',
    descricaoLonga:
      'O massageador tipo pistola (percussão) virou referência entre quem treina e entre equipes que precisam recuperar musculatura rapidamente entre tarefas. A Lunins oferece esse formato para quem quer intensidade ajustável e alcance em costas, glúteos e pernas. Diferente do manual tradicional, a pistola entrega impactos de alta frequência com pouco esforço do usuário — ideal para quem já viu tutoriais no Instagram e quer replicar protocolos curtos de 5 a 10 minutos.',
    beneficios: [
      'Recuperação muscular pós-esforço e alívio de rigidez em grupos musculares grandes.',
      'Bateria recarregável na maioria dos modelos — mobilidade total.',
      'Vários níveis de intensidade para adaptar a sensibilidade de cada pessoa.',
      'Didática forte em vídeo: acompanhe @luninsbr para ângulos seguros de aplicação.',
    ],
    indicadoPara:
      'Quem pratica atividade física regularmente; trabalhadores braçais com fadiga muscular; fãs de conteúdo de performance e bem-estar.',
    cuidados:
      'Não aplique sobre ossos, coluna vertebral de forma direta ou regiões com varizes inflamadas. Crianças, gestantes e pessoas com condições ortopédicas devem seguir orientação médica.',
  },
  {
    id: 'esteira-massageadora-still',
    imagemArquivo: '/images/products/fisiopower/esteira-massageadora-still-p28.webp',
    imagemNuvemUrl:
      'https://salescdn.net/3ncJ_qlh66KIunRnL5cPL0XFbvA=/adaptive-fit-in/500x0/prod/store/13248/medias/products/esteira-massageadora-still-a5335430-62d5-4e4f-b83a-fa121f8d8e83.webp',
    nome: 'Esteira Massageadora Still',
    categoria: 'Vibroterapia',
    precoAVista: 1999,
    parcelas: 12,
    parcelaValor: 169,
    destaque: 'De R$ 2.859 por R$ 1.999 em ações · cobertura ampla reclinada',
    fabricanteReferencia: 'Referência FisioPower — ficha completa no atendimento Lunins.',
    descricaoLonga:
      'A Esteira Massageadora Still é uma opção prática para quem quer uma experiência reclinada de relaxamento. Ela atende bem clientes que preferem deitar ou apoiar o corpo para uma pausa mais completa, especialmente após longos períodos sentado ou de pé. É um produto forte para demonstração porque a pessoa sente rapidamente a diferença entre uma pausa comum e uma pausa estruturada.',
    beneficios: [
      'Formato esteira para relaxamento em postura reclinada.',
      'Boa opção para montar cantinho de descompressão em casa ou no escritório.',
      'Demonstração simples: poucos minutos já ajudam o cliente a entender o valor.',
      'Combina com rotina de pausas programadas e autocuidado.',
    ],
    indicadoPara:
      'Quem prefere relaxar deitado ou reclinado; clientes que querem cobertura maior que almofada ou colete.',
    cuidados:
      'Use em superfície estável e respeite o tempo recomendado. Interrompa em caso de dor, formigamento intenso ou desconforto.',
  },
  {
    id: 'esteira-vibro-neck-shoulder',
    imagemArquivo: '/images/catalogo/esteira-vibro-neck-shoulder.jpg',
    imagemNuvemUrl:
      'https://salescdn.net/jI-G7_t5NGxsl2Dj8ftiuZS3De8=/adaptive-fit-in/500x0/prod/store/13248/medias/products/esteira-de-massagem-vibro-neck-shoulder-bivolt-ea4f76c5-44a2-4e44-8f9b-75e27789813c.webp',
    nome: 'Esteira de Massagem Vibro Neck Shoulder',
    categoria: 'Vibroterapia',
    precoAVista: 2499,
    parcelas: 12,
    parcelaValor: 208.25,
    descricaoLonga:
      'A esteira de massagem com foco em pescoço e ombros utiliza vibroterapia para soltar a musculatura superior das costas — região crítica para quem trabalha com triplo esforço: mental (telas), emocional (liderança) e postural (escrivaninha). Em projetos institucionais Lunins, a vibroterapia aparece como uma das tecnologias mais aceitas porque o efeito é imediato e não exige despir-se ou deitar em maca. Na residência, o equipamento cria um “cantinho de descompressão” que lembra as salas corporativas: continuidade da experiência de marca.',
    beneficios: [
      'Foco em neck e shoulder — alívio onde mais se acumula tensão em escritórios.',
      'Vibração com padrões que podem incluir calor em alguns modelos (confira ficha do produto no fechamento).',
      'Encaixa em rotina de pausas ativas recomendadas por SESMT e iniciativas de saúde do trabalhador.',
      'Combinável com áudios de meditação ou playlists institucionais usadas nas ações Lunins.',
    ],
    indicadoPara:
      'Gestores que querem replicar a experiência de sala Lunins em casa; equipes de TI e jurídico; presentes para alto valor simbólico em campanhas internas.',
    cuidados:
      'Posicione o corpo conforme manual para não forçar cervical. Interrompa se sentir dormência ou dor irradiada.',
  },
  {
    id: 'car-relax-shoulder',
    imagemArquivo: '/images/home/produtos/car-relax-shoulder.png',
    imagemNuvemUrl:
      'https://salescdn.net/fpP5X-H63OC1mUQHclm7haIC1Es=/adaptive-fit-in/500x0/prod/store/13248/medias/products/car-relax-shoulder-ecedd4a4-437c-4db2-a685-9eb8fb2f11a6.webp',
    nome: 'Car Relax Shoulder',
    categoria: 'Assentos',
    precoAVista: 4299,
    parcelas: 12,
    parcelaValor: 358.25,
    destaque: 'Assento de relaxamento para costas e ombros',
    fabricanteReferencia: 'Referência FisioPower — curadoria Lunins.',
    descricaoLonga:
      'O Car Relax Shoulder é um assento de relaxamento voltado para costas, ombros e rotina de pausa. É diferente do Car America (Carzin) e diferente do Assento Shiatsu Premium (Carzão). A proposta é entregar conforto em cadeira, sofá ou ambiente de trabalho, com alto valor percebido para quem sente tensão na região superior das costas.',
    beneficios: [
      'Boa opção para tensão em costas e ombros.',
      'Formato de assento: fácil de posicionar em cadeira ou sofá.',
      'Produto forte para ancoragem por parcela e demonstração prática.',
      'Ajuda a criar rotina de pausa em casa ou no escritório.',
    ],
    indicadoPara:
      'Clientes que querem relaxamento em cadeira/sofá com foco em ombros e costas, sem partir direto para a linha premium Carzão.',
    cuidados:
      'Verifique apoio e estabilidade antes de usar. Não use dormindo, em superfície instável ou com dor aguda sem orientação profissional.',
  },
  {
    id: 'cadeira-shiatsu-still',
    imagemArquivo: '/images/home/produtos/cadeira-shiatsu-still.png?v=2',
    imagemNuvemUrl:
      'https://salescdn.net/wOahRPV7n9JtaCR7JphXP9Iah70=/adaptive-fit-in/0x0/prod/store/13248/medias/products/cadeira-shiatsu-still-a1b4d58e-5149-4491-a33b-d3d9501fb596.webp',
    nome: 'Cadeira Shiatsu Still',
    categoria: 'Assentos',
    precoAVista: 6599,
    parcelas: 12,
    parcelaValor: 549.92,
    destaque: 'Cadeira completa da linha Still',
    fabricanteReferencia: 'Referência AST1000 — FisioPower, curadoria Lunins.',
    descricaoLonga:
      'A Cadeira Shiatsu Still é um equipamento de estrutura própria — diferente do assento Still, que se encosta em outra cadeira ou banco. Foi pensada para relaxamento e alívio de tensão e fadiga, com massagem tipo shiatsu e sistema de vibração integrado. O painel no braço permite acionar pescoço, costas e assento de forma independente, com três intensidades no assento.',
    beneficios: [
      'Cadeira completa: não depende de outra poltrona ou banco.',
      'Massagem shiatsu no encosto, com calor.',
      'Vibração no assento em 3 intensidades (fraca, média e forte).',
      'Painel no braço: pescoço, costas e assento.',
      'Estrutura ajustável, com trava no mecanismo do encosto.',
    ],
    indicadoPara:
      'Salas de descompressão e quem quer uma estação de massagem completa, sem usar o assento avulso em outra cadeira.',
    cuidados:
      'Verifique apoio e trava do encosto antes de usar. Não use dormindo, em superfície instável ou com dor aguda sem orientação profissional.',
  },
  {
    id: 'cadeira-reclinavel',
    imagemArquivo: '/images/catalogo/cadeira-reclinavel.png',
    nome: 'Cadeira reclinável',
    categoria: 'Assentos',
    precoAVista: 950,
    parcelas: 12,
    parcelaValor: 79.17,
    destaque: 'Apoio para a esteira e os aparelhos da sala',
    fabricanteReferencia: 'Complemento da Sala de Descompressão — curadoria Lunins.',
    descricaoLonga:
      'A cadeira reclinável dobrável é o suporte da sala para usar os aparelhos com o corpo apoiado — em especial a Esteira Vibro Neck Shoulder. Estrutura metálica e tecido mesh respirável, pensada para sessões de descompressão sem improvisar um canto. Não é um massageador: é o lugar em que a massagem acontece com estabilidade.',
    beneficios: [
      'Reclinável e dobrável, para guardar depois do uso.',
      'Tecido mesh respirável e estrutura metálica.',
      'Combinada com a esteira de massagem e os demais aparelhos da sala.',
      'Apoio ergonômico para pausas reclinadas, sem ocupar uma poltrona fixa.',
    ],
    indicadoPara:
      'Salas de descompressão e quem vai usar esteira ou massageadores sentado/reclinado, com um apoio digno e estável.',
    cuidados:
      'Abra e trave a estrutura antes de sentar. Use em piso nivelado. Não é um equipamento de massagem — o relaxamento vem dos aparelhos apoiados nela.',
  },
];

export function formatBRL(n: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

/** URL da imagem: prioriza o arquivo local da vitrine; CDN só como reserva */
export function imagemProduto(item: ItemDestaque): string | undefined {
  return item.imagemArquivo ?? item.imagemNuvemUrl;
}

/** Produtos descontinuados — não aparecem na loja nem por URL direta */
export const PRODUTOS_INDISPONIVEIS_IDS = ['foot-pro-relax'] as const;

export function isProdutoIndisponivel(slug: string): boolean {
  return (PRODUTOS_INDISPONIVEIS_IDS as readonly string[]).includes(slug);
}

/** UUID v4 do Supabase — se não for, tratamos como slug do catálogo estático */
export function isProdutoUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

export function getItemDestaquePorSlug(slug: string): ItemDestaque | undefined {
  if (isProdutoIndisponivel(slug)) return undefined;
  return ITENS_DESTAQUE.find((i) => i.id === slug);
}

/** Normaliza nome para comparar vitrine (DB) com catálogo estático */
function normalizeNomeMatch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const VITRINE_ALIAS_TO_SLUG: Record<string, ItemDestaque['id']> = {
  [normalizeNomeMatch('Massageador Manual')]: 'massageador-manual',
  [normalizeNomeMatch('Massageador Manual (com fio)')]: 'massageador-manual',
  [normalizeNomeMatch('Manual com fio')]: 'massageador-manual',
  [normalizeNomeMatch('Manual sem fio')]: 'massageador-relax-confort-sem-fio',
  [normalizeNomeMatch('Massageador Relax Confort sem fio')]: 'massageador-relax-confort-sem-fio',
  [normalizeNomeMatch('Massageador Relaxetion Pro')]: 'massageador-relax-confort-sem-fio',
  [normalizeNomeMatch('Cinta Massageadora FisioPower')]: 'cinta-fisiopower',
  [normalizeNomeMatch('Cinta Modeladora')]: 'cinta-fisiopower',
  [normalizeNomeMatch('Colete Shiatsu')]: 'colete-shiatsu',
  [normalizeNomeMatch('Almofada Lumina')]: 'almofada-shiatsu-lumina',
  [normalizeNomeMatch('Almofada Shiatsu Lumina')]: 'almofada-shiatsu-lumina',
  [normalizeNomeMatch('Almofada Shiatsu Confort 3D')]: 'almofada-shiatsu-lumina',
  [normalizeNomeMatch('Almofada Confort 3D')]: 'almofada-shiatsu-lumina',
  [normalizeNomeMatch('Almofada Infra Blue')]: 'almofada-shiatsu-roller',
  [normalizeNomeMatch('Almofada Infra-Blue')]: 'almofada-shiatsu-roller',
  [normalizeNomeMatch('Assento Shiatsu')]: 'assento-shiatsu-premium',
  [normalizeNomeMatch('Assento Shiatsu Premium')]: 'assento-shiatsu-premium',
  [normalizeNomeMatch('Car America')]: 'car-america',
  [normalizeNomeMatch('Carzin')]: 'car-america',
  [normalizeNomeMatch('Foot')]: 'massageador-pes-shiatsu-pro',
  [normalizeNomeMatch('Massageador para os pes Shiatsu Pro')]: 'massageador-pes-shiatsu-pro',
  [normalizeNomeMatch('Foot Pro Relax')]: 'foot-pro-relax',
  [normalizeNomeMatch('Foot Massager Pro Relax')]: 'foot-pro-relax',
  [normalizeNomeMatch('Massageador para os pes Pro Relax')]: 'foot-pro-relax',
  [normalizeNomeMatch('Plataforma de Reflexologia')]: 'plataforma-reflexologia',
  [normalizeNomeMatch('Plataforma Foot Massager')]: 'plataforma-reflexologia',
  [normalizeNomeMatch('Massageador Pistola')]: 'massageador-pistola',
  [normalizeNomeMatch('Esteira Massageadora Still')]: 'esteira-massageadora-still',
  [normalizeNomeMatch('Esteira de Vibroterapia')]: 'esteira-vibro-neck-shoulder',
  [normalizeNomeMatch('Esteira de Massagem Vibro Neck Shoulder')]: 'esteira-vibro-neck-shoulder',
  [normalizeNomeMatch('Car Relax Shoulder')]: 'car-relax-shoulder',
  [normalizeNomeMatch('Cadeira Shiatsu Still')]: 'cadeira-shiatsu-still',
  [normalizeNomeMatch('Cadeira de Massagem Still')]: 'cadeira-shiatsu-still',
  [normalizeNomeMatch('Cadeira reclinavel')]: 'cadeira-reclinavel',
  [normalizeNomeMatch('Cadeira reclinável')]: 'cadeira-reclinavel',
};

/**
 * Associa uma linha da vitrine (slug ou UUID + nome vindo do Supabase) ao item estático com foto/copy.
 * Usado em /loja e /loja/:id quando `list_produtos_vitrine` devolve UUID.
 */
export function resolveItemDestaqueVitrine(row: { id: string; nome: string }): ItemDestaque | undefined {
  if (!row.nome?.trim()) return undefined;

  if (!isProdutoUuid(row.id)) {
    const bySlug = getItemDestaquePorSlug(row.id);
    if (bySlug) return bySlug;
  }

  const n = normalizeNomeMatch(row.nome);
  if (!n) return undefined;

  const aliasedSlug = VITRINE_ALIAS_TO_SLUG[n];
  if (aliasedSlug) {
    const byAlias = getItemDestaquePorSlug(aliasedSlug);
    if (byAlias) return byAlias;
  }

  for (const item of ITENS_DESTAQUE) {
    const itemN = normalizeNomeMatch(item.nome);
    if (itemN === n) return item;
  }

  const minLen = 12;
  for (const item of ITENS_DESTAQUE) {
    const itemN = normalizeNomeMatch(item.nome);
    if (itemN.length < minLen && n.length < minLen) continue;
    if (n.includes(itemN) || itemN.includes(n)) {
      if (Math.min(n.length, itemN.length) >= minLen) return item;
    }
  }

  const tokensN = new Set(n.split(' ').filter((t) => t.length > 3));
  let best: ItemDestaque | undefined;
  let bestScore = 0;
  for (const item of ITENS_DESTAQUE) {
    const itemN = normalizeNomeMatch(item.nome);
    const tokensItem = itemN.split(' ').filter((t) => t.length > 3);
    let score = 0;
    for (const t of tokensItem) {
      if (tokensN.has(t)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }
  if (best && bestScore >= 2) return best;

  return undefined;
}

/** URL da imagem para card/lista quando o produto veio do RPC com UUID */
export function imagemProdutoVitrineRow(row: { id: string; nome: string }): string | undefined {
  const item = resolveItemDestaqueVitrine(row);
  return item ? imagemProduto(item) : undefined;
}

/** Linha para a vitrine quando o Supabase não está populado */
export function destaqueParaVitrine(item: ItemDestaque) {
  return {
    id: item.id,
    nome: item.nome,
    categoria: item.categoria,
    preco_venda: item.precoAVista,
    descricao: `ou ${item.parcelas}x de ${formatBRL(item.parcelaValor)} s/ juros (ref.)`,
  };
}

export function getItensLojaPublica(): ItemDestaque[] {
  return ITENS_LOJA_PUBLICA_IDS.map((id) => getItemDestaquePorSlug(id)).filter(
    (item): item is ItemDestaque => item != null
  );
}

/** Texto genérico quando o produto vem só do banco (UUID) sem match no catálogo estático */
export const TEXTO_PRODUTO_GENERICO = {
  paragrafo:
    'Este item faz parte do catálogo oficial Lunins. Para especificações técnicas completas, tutoriais em vídeo e condições comerciais da campanha vigente, fale com nosso time ou consulte o material enviado pelo seu consultor. No Instagram @luninsbr você encontra demonstrações em vídeo da linha de equipamentos.',
} as const;
